/**
 * Strongly-typed event payload schemas.
 *
 * These travel through:
 *   - Redis Streams (cross-pod realtime)
 *   - NestJS EventEmitter (in-process)
 *   - Outbox table (durable cross-service)
 *
 * Naming convention: {domain}.{past-tense-verb}
 */
import { z } from 'zod';
import { LeadStatus, AccountStatus, FilterStage } from '../enums.js';
// ----- raw message ingest -----
export const RawMessageReceivedEvent = z.object({
    type: z.literal('raw_message.received'),
    rawMessageId: z.string().uuid(),
    tenantId: z.string().uuid(),
    accountId: z.string().uuid(),
    groupId: z.string().uuid(),
    tgChatId: z.string(),
    tgMessageId: z.string(),
    senderUserId: z.string(),
    text: z.string(),
    ts: z.string().datetime(),
    fencingToken: z.number().int(),
});
// ----- filter pipeline -----
export const FilterHitEvent = z.object({
    type: z.literal('filter.hit'),
    stage: FilterStage,
    rawMessageId: z.string().uuid(),
    tenantId: z.string().uuid(),
    configVersionId: z.string().uuid(),
    score: z.number().optional(),
    matchedTerms: z.array(z.string()).optional(),
});
// ----- lead -----
export const LeadDetectedEvent = z.object({
    type: z.literal('lead.detected'),
    leadId: z.string().uuid(),
    tenantId: z.string().uuid(),
    targetUserId: z.string(),
    triggeredByAccountId: z.string().uuid(),
    configVersionId: z.string().uuid(),
});
export const LeadStateChangedEvent = z.object({
    type: z.literal('lead.state_changed'),
    leadId: z.string().uuid(),
    tenantId: z.string().uuid(),
    fromStatus: LeadStatus,
    toStatus: LeadStatus,
    version: z.number().int(),
    actor: z.string(), // sales_id | 'system'
    ts: z.string().datetime(),
});
// ----- outbound dm -----
export const DmSentEvent = z.object({
    type: z.literal('dm.sent'),
    leadId: z.string().uuid(),
    conversationId: z.string().uuid(),
    messageId: z.string().uuid(),
    accountId: z.string().uuid(),
    tgMessageId: z.string(),
    ts: z.string().datetime(),
});
export const DmReceivedEvent = z.object({
    type: z.literal('dm.received'),
    conversationId: z.string().uuid(),
    messageId: z.string().uuid(),
    accountId: z.string().uuid(),
    tgMessageId: z.string(),
    text: z.string(),
    ts: z.string().datetime(),
});
// ----- account health -----
export const AccountStatusChangedEvent = z.object({
    type: z.literal('account.status_changed'),
    accountId: z.string().uuid(),
    fromStatus: AccountStatus,
    toStatus: AccountStatus,
    reason: z.string(),
    ts: z.string().datetime(),
});
// ----- lead assignment -----
export const LeadAssignedEvent = z.object({
    type: z.literal('lead.assigned'),
    leadId: z.string().uuid(),
    tenantId: z.string().uuid(),
    assignedSalesId: z.string().uuid(),
    version: z.number().int(),
    ts: z.string().datetime(),
});
// ----- notification created (outbox → dispatcher → ws) -----
export const NotificationCreatedEvent = z.object({
    type: z.literal('notification.created'),
    notificationId: z.string().uuid(),
    recipientSalesId: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    notificationType: z.string(),
    payloadJson: z.record(z.unknown()),
    ts: z.string().datetime(),
});
// ----- group joined -----
export const GroupJoinedEvent = z.object({
    type: z.literal('group.joined'),
    groupId: z.string().uuid(),
    tenantId: z.string().uuid(),
    joinedByAccountId: z.string().uuid(),
    silentUntil: z.string().datetime(),
    ts: z.string().datetime(),
});
// ----- cascade trace (admin debugging visibility) -----
export const CascadeTraceEvent = z.object({
    type: z.literal('cascade.trace'),
    id: z.string().uuid(),
    ts: z.string().datetime(),
    rawMessageId: z.string().uuid(),
    tenantId: z.string().uuid(),
    accountId: z.string().uuid(),
    groupId: z.string().uuid(),
    verdict: z.enum([
        'passed_l1',
        'dropped_l2',
        'passed_l2',
        'dropped_l3',
        'lead',
    ]),
    cosine: z.number().nullable(),
    bestNoiseAnchorId: z.string().uuid().nullable(),
    fastConfidence: z.number().nullable(),
    preciseConfidence: z.number().nullable(),
    textSnippet: z.string(),
    senderUsername: z.string().nullable(),
    leadId: z.string().uuid().nullable(),
});
// ----- discriminated union for stream consumers -----
export const PlatformEvent = z.discriminatedUnion('type', [
    RawMessageReceivedEvent,
    FilterHitEvent,
    LeadDetectedEvent,
    LeadStateChangedEvent,
    LeadAssignedEvent,
    NotificationCreatedEvent,
    GroupJoinedEvent,
    DmSentEvent,
    DmReceivedEvent,
    AccountStatusChangedEvent,
    CascadeTraceEvent,
]);
// ----- stream / queue names (single source of truth) -----
export const StreamNames = {
    rawMessages: 'stream:raw_messages',
    kwHit: 'stream:kw_hit',
    vecHit: 'stream:vec_hit',
    leadCandidates: 'stream:lead_candidates',
    wsBroadcast: 'stream:ws_broadcast',
    cascadeTraces: 'stream:cascade_traces',
};
export const QueueNames = {
    outboundDm: 'queue:outbound_dm',
    joinGroup: 'queue:join_group',
    healthProbe: 'queue:health_probe',
    schemaMigration: 'queue:schema_migration',
};
//# sourceMappingURL=index.js.map